import * as _ from "lodash";
import React, { useState, useEffect } from "react";
import * as Models from "../../../Models";
import DataGrid from "../DataGrid";
import { getItemGroupData } from "../../../Helpers/table";
import {
    TYPE_SITE,
    TYPE_PLANT,
    TYPE_PLANTUNIT,
    TYPE_MODULE,
    TYPE_EQUIPMENT,
    TYPE_COMPONENT,
    CHEVRON_DOWN,
} from "../../../Constants";
import { OutotecIcons } from "../Icons";
import SelectColumns, { ISelectColumn } from "./SelectColumns";
import {
    getSiteAssetsColumnState,
    setSiteAssetsColumnState,
} from "../../../Helpers/localStorage";
import filterItemsStore from "../../../Store/filterItems";

interface ISiteAssetsItemGroupProps {
    dataGroupName: string;
    attributeGroup: Models.AttributeGroup;
    match: Models.Match;
    // activeDetail: Models.ActiveDetail;
}

export default function SiteAssetsItemGroup(props: ISiteAssetsItemGroupProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [columnOptions, setColumnOptions] = useState<ISelectColumn[]>([]);
    const getActiveDetail = filterItemsStore((s) => s.getActiveDetail);
    const activeDetail = getActiveDetail(props.match.params);

    useEffect(() => {
        const hiddenDefaultColumns = [
            "Plant",
            "Plant Unit",
            "Module",
            "Equipment",
            "Sub Equipment",
        ];
        const visibleHeaders: string[] = getVisibleHeaders(
            props.attributeGroup.headers,
            activeDetail
        );
        const columnOptions: any = visibleHeaders.map((h) => {
            return {
                value: h,
                isEnabled: h !== "Asset ID" && h !== "Asset Description",
                isSelected: !_.includes(hiddenDefaultColumns, h),
            } as ISelectColumn;
        });

        columnOptions.forEach((columnOption) => {
            var stored: ISelectColumn = getSiteAssetsColumnState(columnOption);
            columnOption.isSelected = stored.isSelected;
        });

        //    setColumns(columnOptions);
        setColumnOptions(columnOptions);
    }, []);

    const showHideColumn = (col: ISelectColumn) => {
        let updatedColumn: ISelectColumn | undefined;

        setColumnOptions((prevCols) => {
            const newCols = prevCols.map((c) => {
                if (c.value === col.value) {
                    updatedColumn = { ...c, isSelected: !c.isSelected };
                    return updatedColumn;
                }
                return c;
            });

            return newCols;
        });
        setSiteAssetsColumnState(updatedColumn);
    };

    function getDataGridContent(
        headers: string[],
        visibleHeaders: string[],
        rows: Models.AttributeRow[]
    ): Models.DataGridContent {
        insertSummaryRow(rows);

        const result: Models.DataGridContent = getItemGroupData(headers, rows);

        result.columns.forEach((col) => {
            col.minWidth = 100;
            col.filterMethod = filterCaseInsensitive;
            col.sortMethod = sortByDescription;
            if (visibleHeaders.indexOf(col.Header) < 0) {
                col.show = false;
            }

            let selectedColumn: ISelectColumn = columnOptions.find(
                (c) => c.value === col.Header
            );
            if (selectedColumn && !selectedColumn.isSelected) {
                col.show = false;
            }
        });

        const assetDescCol: any = result.columns.find(
            (c) => c.Header === "Asset Description"
        );
        if (assetDescCol) {
            assetDescCol.fixed = "left";
            assetDescCol.width = 150;
        }

        const assetIdCol: any = result.columns.find(
            (c) => c.Header === "Asset ID"
        );
        if (assetIdCol) {
            assetIdCol.fixed = "left";
            assetIdCol.width = 125;
        }

        return result;
    }

    function getVisibleHeaders(
        headers: string[],
        activeDetail: Models.ActiveDetail
    ): string[] {
        var visibleHeaders: string[] = [...headers];

        if (activeDetail.detail.type === TYPE_SITE) {
            // remove nothing
        } else if (activeDetail.detail.type === TYPE_PLANT) {
            _.remove(visibleHeaders, (h: string) => {
                return h === TYPE_PLANT;
            });
        } else if (activeDetail.detail.type === TYPE_PLANTUNIT) {
            _.remove(visibleHeaders, (h: string) => {
                return h === TYPE_PLANT || h === TYPE_PLANTUNIT;
            });
        } else if (activeDetail.detail.type === TYPE_MODULE) {
            _.remove(visibleHeaders, (h: string) => {
                return (
                    h === TYPE_PLANT ||
                    h === TYPE_PLANTUNIT ||
                    h === TYPE_MODULE
                );
            });
        } else if (activeDetail.detail.type === TYPE_EQUIPMENT) {
            _.remove(visibleHeaders, (h: string) => {
                return (
                    h === TYPE_PLANT ||
                    h === TYPE_PLANTUNIT ||
                    h === TYPE_MODULE ||
                    h === TYPE_EQUIPMENT
                );
            });
        } else if (activeDetail.detail.type === TYPE_COMPONENT) {
            // Sub Equipment
            _.remove(visibleHeaders, (h: string) => {
                return (
                    h === TYPE_PLANT ||
                    h === TYPE_PLANTUNIT ||
                    h === TYPE_MODULE ||
                    h === TYPE_EQUIPMENT ||
                    h.toLocaleLowerCase() === "sub equipment"
                );
            });
        }

        return visibleHeaders;
    }

    const sortByDescription: Function = (
        a: Models.AttributeCell,
        b: Models.AttributeCell,
        desc: boolean
    ) => {
        const aValue: string =
            a.url && a.url.description ? a.url.description : a.description;
        const bValue: string =
            b.url && b.url.description ? b.url.description : b.description;

        if ((a as any).isSummaryCell) {
            return !desc ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
        }
        if ((b as any).isSummaryCell) {
            return !desc ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
        }

        return aValue.localeCompare(bValue);
    };

    const filterCaseInsensitive: Function = (filter: any, row: any) => {
        const id: string = filter.pivotId || filter.id;
        const rowValue: any = row[id];
        if (rowValue !== null) {
            const filterValue: string =
                rowValue.url && rowValue.url.description
                    ? rowValue.url.description
                    : rowValue.description;
            return filterValue !== undefined
                ? String(filterValue.toString().toLowerCase()).includes(
                      filter.value.toLowerCase()
                  ) || rowValue.isSummaryCell === true
                : true;
        }
    };

    const insertSummaryRow: Function = (
        gridDataContent: Models.AttributeRow[]
    ) => {
        if (gridDataContent.length > 1) {
            const summaryRow: Models.AttributeRow = _.cloneDeep(
                gridDataContent[0]
            );

            summaryRow.items.forEach(
                (value: Models.AttributeCell, index: number) => {
                    const uniqueValues: any[] = _.uniq(
                        gridDataContent.map((d) => d.items[index].url.id)
                    ).filter((v) => !!v);
                    summaryRow.items[index].description =
                        uniqueValues.length !== 0
                            ? uniqueValues.length.toString()
                            : "";
                    summaryRow.items[index].url = {} as Models.Link;
                    summaryRow.items[index].isSummaryCell = true;
                    summaryRow.statusType =
                        Models.AttributeGroupItemRowStatusType.Active;
                    summaryRow.isSummaryRow = true;
                }
            );
            gridDataContent.splice(0, 0, summaryRow);
        }
    };

    var rows: Models.AttributeRow[] = [...props.attributeGroup.rows];

    const headers: string[] = props.attributeGroup.headers;
    const visibleHeaders: string[] = getVisibleHeaders(headers, activeDetail);

    const dataGridContent: Models.DataGridContent = getDataGridContent(
        headers,
        visibleHeaders,
        rows
    );

    return (
        <div className="ItemGroup ItemGroup--siteassets">
            <h3 className="ItemGroup__title select-columns">
                <div>
                    <span>{props.attributeGroup.name}</span>
                    <span
                        className="select-columns-toggle"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        Columns <OutotecIcons type={CHEVRON_DOWN} />
                    </span>
                </div>
            </h3>
            <SelectColumns
                isOpen={isOpen}
                columnOptions={columnOptions}
                closeHandler={() => setIsOpen(false)}
                clickHandler={showHideColumn}
            />
            <DataGrid
                filterable={dataGridContent.data.length > 10}
                isSortable={true}
                showPagination={false}
                content={dataGridContent}
            />
        </div>
    );
}
